import { ChangeDetectionStrategy, Component, Inject } from "@angular/core";
import { Meeting } from "@common/ADAPT.Common.Model/organisation/meeting";
import { MeetingAgendaItem } from "@common/ADAPT.Common.Model/organisation/meeting-agenda-item";
import { MeetingAgendaTemplate } from "@common/ADAPT.Common.Model/organisation/meeting-agenda-template";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { ADAPT_DIALOG_DATA } from "@common/ux/adapt-common-dialog/adapt-common-dialog.globals";
import { BaseDialogComponent } from "@common/ux/adapt-common-dialog/base-dialog.component/base-dialog.component";
import { timer } from "rxjs";
import { finalize, tap } from "rxjs/operators";
import { MeetingsService } from "../meetings.service";

enum ImportType {
    FromTemplate,
    FromMeeting,
}

interface IImportTypeOption {
    type: ImportType;
    text: string;
}

export interface IImportMeetingAgendaData {
    currentMeeting?: Meeting;
    saveOnClose: boolean;
    recentMeetingOnly?: boolean;
    currentTeam?: Team;
}

@Component({
    selector: "adapt-import-agenda-dialog",
    templateUrl: "./import-agenda-dialog.component.html",
    styleUrl: "./import-agenda-dialog.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class ImportAgendaDialogComponent extends BaseDialogComponent<IImportMeetingAgendaData, MeetingAgendaItem[]> {
    public readonly dialogName = "ImportFromOtherMeetingAgendaDialog";

    public readonly ImportType = ImportType;
    public readonly ImportTypes: IImportTypeOption[] = [{
        type: ImportType.FromMeeting,
        text: "Recent meeting",
    }, {
        type: ImportType.FromTemplate,
        text: "Existing meeting template",
    }];

    public selectedImportType = ImportType.FromMeeting;

    public selectedMeeting?: Meeting;
    public selectedTemplate?: MeetingAgendaTemplate;

    public loadingItems = false;
    public agendaItems: MeetingAgendaItem[] = [];

    public dialogTitle = "Import meeting agenda";

    public constructor(
        @Inject(ADAPT_DIALOG_DATA) public dialogData: IImportMeetingAgendaData,
        private meetingsService: MeetingsService,
    ) {
        super();
        if (dialogData.recentMeetingOnly) {
            this.dialogTitle = "Import recent meeting";
        }
    }

    public onImportTypeChange(importType: ImportType) {
        this.selectedTemplate = undefined;
        this.selectedMeeting = undefined;
        this.selectedImportType = importType;
    }

    public meetingSelected(meeting?: Meeting) {
        if (!meeting) {
            return;
        }

        this.selectedMeeting = meeting;
        this.loadingItems = true;
        this.meetingsService.getAgendaItemsForMeeting(meeting).pipe(
            this.takeUntilDestroyed(),
            finalize(() => this.loadingItems = false),
        ).subscribe((items) => this.agendaItems = items);
    }

    public templateSelected(template?: MeetingAgendaTemplate) {
        if (!template) {
            return;
        }

        this.selectedTemplate = template;
        this.loadingItems = true;
        this.meetingsService.getAgendaItemsForMeetingAgendaTemplate(template.meetingAgendaTemplateId).pipe(
            this.takeUntilDestroyed(),
            finalize(() => this.loadingItems = false),
        ).subscribe((items) => this.agendaItems = items);
    }

    @Autobind
    public importItems() {
        // do resolve in the next digest cycle to workaround unfinished subscriber in blocking click destruction
        return timer(0).pipe(
            tap(() => this.resolve(this.agendaItems)),
        );
    }
}
